import type { Board } from "../board";
import { Direction } from "../direction";
import type { ControllableByAI } from "../interfaces";
import { isPassableThroughDoors } from "../passability";
import type { PhysicalThing } from "../physicalThing";
import { RobotSpawner } from "../things/robotSpawner";
import { WoodenDoor, WoodenDoorState } from "../things/woodenDoor";
import type { XY } from "../xy";

/**
 * When `blockedStep` is unreachable, pick a king-neighbor adjacent to both `here`
 * and `blockedStep` that satisfies `isStandable`, sorted by squared distance to
 * `target`. Used to "go around" an obstacle on the actor's planned path.
 */
export function findSidestepTowards(
	here: XY,
	blockedStep: XY,
	target: XY,
	isStandable: (xy: XY) => boolean,
): XY | null {
	const blockedNeighbors = blockedStep.getNeighbors();
	const alternatives = here
		.getNeighbors()
		.filter((n) => blockedNeighbors.some((bn) => bn.equals(n)))
		.filter(isStandable)
		.sort((a, b) => a.distanceSquared(target) - b.distanceSquared(target));
	return alternatives[0] ?? null;
}

/** Bump a closed wooden door at `xy` open. No-op if the cell isn't a closed door. */
export function bumpDoorIfClosed(board: Board, xy: XY, author: PhysicalThing): void {
	const door = board.getThingOfClass(WoodenDoor, xy);
	if (door === null || door.getState() !== WoodenDoorState.CLOSED) return;
	door.bump(Direction.N, author);
}

/** Update horizontal look direction from a movement delta. Vertical-only moves are no-ops. */
export function updateLookDirectionFromHorizontalDelta(
	actor: ControllableByAI,
	before: XY,
	after: XY,
): void {
	const dx = after.x - before.x;
	if (dx > 0) actor.setLookDirection(Direction.E);
	else if (dx < 0) actor.setLookDirection(Direction.W);
}

/**
 * "Can a mob step here": in-bounds, not the player's tile, no other active
 * non-frozen mob, and passable — treating a wooden door as openable.
 */
export function canMobStandIn(board: Board, xy: XY): boolean {
	if (!board.isInBounds(xy) || board.hasMob(xy)) return false;
	if (board.player?.xy.equals(xy)) return false;
	return isPassableThroughDoors(board, xy);
}

/** Cell or any 8-neighbor contains a wooden door or robot spawner. */
export function isPositionInconvenient(board: Board, xy: XY): boolean {
	const area = xy.getNeighbors();
	area.push(xy);
	return area
		.filter((p) => board.isInBounds(p))
		.some(
			(p) =>
				board.getThingOfClass(WoodenDoor, p) !== null ||
				board.getThingOfClass(RobotSpawner, p) !== null,
		);
}
